import type { JSX, PropsWithChildren } from 'react';
import { useRef } from 'react';

import { Link } from '@change/ds-core-ui/next';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { getPetitionQRCode, type PetitionQRCodeResult } from 'src/app/shared/hooks/qrcode';

type PetitionQRCodeDownloadLinkProps = PropsWithChildren<{
	slugOrId: string;
	type: QrCodeType;
	size?: number;
	filename?: string;
	onDownload?: () => Promise<void>;
}>;

/**
 * Trigger the file download using a temporary anchor element
 */
async function triggerDownload({
	dataUri,
	filename = 'petition-qrcode.png',
	onDownload,
}: {
	dataUri: string;
	filename?: string;
	onDownload?: () => Promise<void>;
}): Promise<void> {
	const link = document.createElement('a');
	link.setAttribute('href', dataUri);
	link.setAttribute('download', filename);
	const clickEvent = new MouseEvent('click');
	link.dispatchEvent(clickEvent);
	link.remove();

	await onDownload?.();
}

/**
 * Render a link that when clicked, downloads a QR code.
 * The QR code is fetched lazily when the link is clicked.
 */
export function PetitionQRCodeDownloadLink({
	children,
	slugOrId,
	size,
	type,
	filename,
	onDownload,
}: PetitionQRCodeDownloadLinkProps): JSX.Element {
	const utilityContext = useUtilityContext();
	// Used to cache the QR code once it's fetched so that subsequent
	// clicks don't trigger a new fetch.
	const cachedDataRef = useRef<PetitionQRCodeResult | null>(null);
	// Used to track whether the QR code is currently being fetched
	// so that multiple clicks only result in a single fetch.
	const isFetchingRef = useRef(false);

	const handleClick = async (_event: unknown) => {
		if (isFetchingRef.current) return;
		if (cachedDataRef.current) {
			await triggerDownload({ dataUri: cachedDataRef.current.imageBase64Uri, filename, onDownload });
			return;
		}

		isFetchingRef.current = true;
		try {
			const data = await getPetitionQRCode({
				slugOrId,
				type,
				size,
				utilityContext,
			});
			cachedDataRef.current = data;
			await triggerDownload({ dataUri: data.imageBase64Uri, filename, onDownload });
		} finally {
			isFetchingRef.current = false;
		}
	};

	return (
		<Link
			href="#"
			onClick={handleClick}
			bold
			size="small"
			variant="secondary"
			data-qa="qrcode-download-link"
			data-testid="qrcode-download-link"
		>
			{children}
		</Link>
	);
}
